import { useMemo, useState } from 'react';
import { t } from 'i18next';

import PageLayout from '@/components/Layout/PageLayout';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { ExportDataDropDown } from '@/components/molecules/ExportDataDropDown/ExportDataDropDown';
import SegmentedControl from '@/components/molecules/SegmentedControl';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import { LICENSES } from '@/config/licenses';
import { usePermissions } from '@/contexts/auth';
import { useCage, useCageName, useCageTimezone } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { isValidCageId } from '@/domain/cages';
import { fullSizeImageMapper, useGetWelfareImages } from '@/domain/images';
import {
    selectMeasurementSource,
    selectSnapshotMeasurementSource,
    useGetCageDailyLice,
    useGetCageLast200Lice,
} from '@/domain/lice';
import { useImageState } from '@/hooks/image-state';
import { useQueryParams } from '@/hooks/useQueryParams';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { dayToString } from '@/utils/date';
import { isChileanLiceType } from '@/utils/locationTypeHelpers';
import { createCageUrl } from '@/utils/urlGenerator';

import { buildLiceDownload } from '../liceDownload';

import CageLiceDashboardPageContent from './CageLiceDashboardPageContent';

import styles from './CageLiceDashboard.module.scss';

export default function CageLiceDashboardContainer({
    clientId,
    locationId,
    cageId,
}: CagePageInterface) {
    const { client, hasCageLicense, location, stock } = useCage();
    const cageName = useCageName();
    const timezone = useCageTimezone();
    const { isOptoScaleAdmin } = usePermissions();
    const { from, to, daypartTag, selectedFishGroupId } = useCageFilter();

    const isChileanLice = isChileanLiceType(stock?.liceType);

    const defaultMeasurmentSource = client?.featureFlags?.includes('LICE_LEGACY_VIEW')
        ? 'raw'
        : 'legacy';

    const showToggle = isOptoScaleAdmin ? true : !isChileanLice;

    const [measurementSource, setMeasurementSource] = useState<'raw' | 'legacy'>(
        defaultMeasurmentSource
    );

    const licePageUrl =
        client && location ? `${createCageUrl(client.id, location.id, cageId)}/lice` : undefined;

    const dayFrom = from ? dayToString(from) : undefined;
    const dayTo = to ? dayToString(to) : undefined;

    const queryParams = useQueryParams();

    const dailyLice = useGetCageDailyLice(
        cageId,
        { dayFrom, dayTo, dayPart: daypartTag, fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );
    const last200 = useGetCageLast200Lice(
        cageId,
        { fishGroupId: selectedFishGroupId },
        { staleTime: 5 * 60000 }
    );

    const imagesFishGroup = usePenCohortFishGroup();
    const imagesQuery = useGetWelfareImages(
        cageId,
        { type: 'louse-image', numberOfImages: '1' },
        {
            enabled: isValidCageId(cageId),
            keepPreviousData: true,
            fishGroup: imagesFishGroup,
        }
    );
    const {
        data: imagesdata,
        isLoading: imagesIsLoading,
        isFetching: imagesIsFetching,
        error: imageError,
    } = imagesQuery;

    const imageState = useImageState({
        data: imagesdata,
        // `useMemo`, not `useCallback`: the argument is a *call* that returns the mapper, not
        // the mapper's body. They are equivalent at runtime, but `useCallback` cannot see that
        // `cageId` is used, so the linter reads the dependency as unnecessary — and an autofix
        // dropping it pins the mapper to the pen it first mounted with, mapping later rows to
        // the previous pen's image URL.
        mapper: useMemo(() => fullSizeImageMapper(cageId), [cageId]),
        loading: imagesIsLoading && !imageError,
        // `keepPreviousData` means a switch to another pen is a *fetch*, not a *load*:
        // react-query keeps `isLoading` false because it still holds the previous pen's
        // response. Without this the card saw "not loading, no image" and said the pen had no
        // detections, while the new one was still on its way — and had the previous pen's
        // image survived the remap, it would have shown that instead, which is worse.
        isFetching: imagesIsFetching,
        timezone: timezone,
        queryParam: 'imageId',
    });

    const isLoading = dailyLice.isLoading || last200.isLoading;
    const error = dailyLice.error || last200.error;

    const liceDownload = buildLiceDownload({
        rows: dailyLice.data,
        daypart: queryParams[0]?.d,
        t,
        isChileanLice,
    });

    return (
        <PageLayout
            title={cageName}
            subtitle={t('common.labels.lice')}
            actions={
                <div className={styles.pageTitleRowButtonContainer}>
                    <ExportDataDropDown>
                        <DownloadOptions
                            data={liceDownload.data}
                            columns={liceDownload.columns}
                            nameToInclude={liceDownload.nameToInclude}
                        />
                    </ExportDataDropDown>
                    <div>
                        {showToggle && (
                            <SegmentedControl<'raw' | 'legacy'>
                                defaultValue={measurementSource}
                                onChange={(value) => setMeasurementSource(value)}
                                options={[
                                    {
                                        value: 'raw',
                                        label: t('common.labels.rawData'),
                                        tooltip: t('common.labels.rawdataexplain'),
                                    },
                                    {
                                        value: 'legacy',
                                        label: t('common.labels.simulatedManualCount'),
                                        tooltip: t('common.labels.reportdataexplain'),
                                    },
                                ]}
                            />
                        )}
                    </div>
                </div>
            }>
            <CageLiceDashboardPageContent
                clientId={clientId}
                locationId={locationId}
                cageId={cageId}
                last200={
                    last200.data
                        ? selectSnapshotMeasurementSource(last200.data, measurementSource)
                        : null
                }
                liceCount={dailyLice.data.map((row) =>
                    selectMeasurementSource(row, measurementSource)
                )}
                licePageUrl={licePageUrl}
                images={imageState.images}
                liceImagesLoading={imageState.loading || Boolean(imageState.isFetching)}
                liceImagesError={imageError ? retryFailedQueries(imagesQuery) : null}
                last200Error={last200.error ? retryFailedQueries(last200) : null}
                hasLiceLicense={hasCageLicense(LICENSES.LICE_COUNT.MIN)}
                isChileanLice={isChileanLice}
                isLoading={isLoading}
                error={error}
                retry={retryFailedQueries(dailyLice, last200)}
            />
        </PageLayout>
    );
}
